import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { useAppSelector } from './hooks/redux-hooks';
// Layouts (eager load for immediate display)
import AuthLayout from './layouts/Auth';
import MainLayout from './layouts/Main';
import QuizHome from './components/main/quiz';

// Lazy load all page components
const Login = lazy(() => import('./components/Login'));
const Dashboard = lazy(() => import('./components/main/dashboard/Dashboard'));
const EnterpriseUsers = lazy(
  () => import('./components/main/enterprise-users/EnterpriseUsers')
);
const UserGroups = lazy(() => import('./components/main/user-groups/UserGroups'));
const EnterpriseChatData = lazy(
  () => import('./components/main/enterprise-users/EnterpriseUserChatData')
);
// const AiModels = lazy(() => import('./components/main/ai-models/AiModels'));
const AssistantList = lazy(
  () => import('./components/main/assistants/AssistantList')
);
const CreateAssistant = lazy(
  () => import('./components/main/assistants/ManageAssistant')
);
const CreateAgent = lazy(() => import('./components/main/agents/CreateAgent'));
const ConnectorsList = lazy(
  () => import('./components/main/connectors/ConnectorsList')
);
const Lms = lazy(() => import('./components/main/lms/Lms'));
const AgentList = lazy(() => import('./components/main/agents/AgentsList'));
const PublicAssistants = lazy(
  () => import('./components/main/public-assistants/PublicAssistants')
);
const AssistantMetricsDashboard = lazy(
  () =>
    import('./components/main/assistants/metrics-dashboard/AssistantMetricsDashboard')
);
const AgentMetricsDashboard = lazy(
  () =>
    import('./components/main/agents/metrics-dashboard/AgetntMetricsDashboard')
);
const Grading = lazy(() => import('./components/main/grading/Grading'));
const CreateSession = lazy(
  () => import('./components/main/grading/CreateSession')
);
const GradeSessionDetail = lazy(
  () => import('./components/main/grading/GradeSessionDetail')
);
const AiTutorList = lazy(
  () => import('./components/main/ai-tutors/AiTutorList')
);
const ManageAiTutor = lazy(
  () => import('./components/main/ai-tutors/ManageAiTutor')
);
const CreateAiTutor = lazy(
  () => import('./components/main/ai-tutors/CreateAiTutorDialog')
);
const TutorMetricsDashboard = lazy(
  () =>
    import('./components/main/ai-tutors/metrics-dashboard/TutorMetricsDashboard')
);
const AIQPage = lazy(() => import('./components/main/aiq/AIQPage'));
const AcraHome = lazy(() => import('./components/main/acra'));
const TokenMgmt = lazy(() => import('./components/main/token-mgmt/TokenMgmt'));

// Simple fallback component
const LazyLoadFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const LmsAdminRoute = () => {
  const { orgInfo, isSuperAdmin } = useAppSelector((state) => state.common);
  if (!orgInfo?.allowLMS || !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Lms />;
};

function App() {
  return (
    <div className="h-[100vh] overflow-hidden">
      <Router>
        <Routes>
          <Route path="" element={<AuthLayout />}>
            <Route
              index
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <Login />
                </Suspense>
              }
            />
          </Route>
          <Route path="/" element={<MainLayout />}>
            <Route
              path="dashboard"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <Dashboard />
                </Suspense>
              }
            />
            <Route
              path="enterprise-users"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <EnterpriseUsers />
                </Suspense>
              }
            />
            <Route
              path="user-groups"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <UserGroups />
                </Suspense>
              }
            />
            <Route
              path="enterprise-users/:id"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <EnterpriseChatData />
                </Suspense>
              }
            />
            {/* <Route path="change-password" element={<ChangePassword />} /> */}
            {/* <Route
              path="ai-models"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <AiModels />
                </Suspense>
              }
            /> */}
            <Route
              path="assistants"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <AssistantList />
                </Suspense>
              }
            />
            <Route
              path="assistants/create-assistant"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <CreateAssistant />
                </Suspense>
              }
            />
            <Route
              path="assistants/:id"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <CreateAssistant />
                </Suspense>
              }
            />
            <Route
              path="assistants/:id/metrics"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <AssistantMetricsDashboard />
                </Suspense>
              }
            />
            <Route
              path="agents"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <AgentList />
                </Suspense>
              }
            />
            <Route
              path="agents/create-agent"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <CreateAgent />
                </Suspense>
              }
            />
            <Route
              path="agents/:agentId"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <CreateAgent />
                </Suspense>
              }
            />
            <Route
              path="agents/:agentId/metrics"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <AgentMetricsDashboard />
                </Suspense>
              }
            />
            <Route
              path="connectors"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <ConnectorsList />
                </Suspense>
              }
            />
            <Route
              path="lms"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <LmsAdminRoute />
                </Suspense>
              }
            />
            <Route
              path="public-assistants"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <PublicAssistants />
                </Suspense>
              }
            />
            <Route
              path="public-assistant/:id"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <PublicAssistants />
                </Suspense>
              }
            />
            <Route
              path="grading"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <Grading />
                </Suspense>
              }
            />
            <Route
              path="grading/new"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <CreateSession />
                </Suspense>
              }
            />
            <Route
              path="grading/session/:sessionId"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <GradeSessionDetail />
                </Suspense>
              }
            />
            <Route
              path="ai-tutors"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <AiTutorList />
                </Suspense>
              }
            />
            <Route
              path="ai-tutors/create"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <CreateAiTutor />
                </Suspense>
              }
            />
            <Route
              path="ai-tutors/:id"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <ManageAiTutor />
                </Suspense>
              }
            />
            <Route
              path="ai-tutors/:id/metrics"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <TutorMetricsDashboard />
                </Suspense>
              }
            />
            <Route
              path="quiz/*"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <QuizHome />
                </Suspense>
              }
            />
            <Route
              path="aiq"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <AIQPage />
                </Suspense>
              }
            />
            <Route
              path="acra/*"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <AcraHome />
                </Suspense>
              }
            />
            <Route
              path="token-management"
              element={
                <Suspense fallback={<LazyLoadFallback />}>
                  <TokenMgmt />
                </Suspense>
              }
            />
          </Route>
        </Routes>
      </Router>
    </div>
  );
}

export default App;
